import { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { X, Globe, Lock, Loader2, AlertTriangle, MapPin, Compass, Trash2 } from "lucide-react";

/**
 * Live slug sanitizer — mirrors backend's CUSTOM_SLUG_RE.
 * Lowercases, replaces umlauts, strips non `[a-z0-9-]`, collapses dashes,
 * trims to 60 chars, drops leading/trailing dashes.
 */
function sanitizeSlug(input) {
  const map = { ä: "ae", ö: "oe", ü: "ue", ß: "ss", Ä: "ae", Ö: "oe", Ü: "ue" };
  return String(input || "")
    .replace(/[äöüÄÖÜß]/g, (c) => map[c] || c)
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "")
    .replace(/-+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);
}

const SLUG_RE = /^[a-z0-9](?:[a-z0-9-]{0,58}[a-z0-9])?$/;

/** Edit modal for a video's title, description, visibility and slug. */
export default function EditModal({ video, onClose, onSaved }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [visibility, setVisibility] = useState("public");
  const [slugInput, setSlugInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Phase 10G — Text-Overlay state (replaces Phase 10D HUD UI)
  const [textOverlays, setTextOverlays] = useState([]);
  // Phase 10G — keep raw legacy HUD overlay around for backwards-compat notice
  const [legacyHud, setLegacyHud] = useState(null);

  // Phase 10E — POI annotations (list, edited in-place)
  const [pois, setPois] = useState([]);

  useEffect(() => {
    if (video) {
      setTitle(video.title || "");
      setDescription(video.description || "");
      setVisibility(video.visibility || "private");
      setSlugInput(video.slug || "");
      setError("");
      // Phase 10G — text overlays
      setTextOverlays(Array.isArray(video.text_overlays) ? [...video.text_overlays] : []);
      setLegacyHud(video.hud_overlay && (video.hud_overlay.location || video.hud_overlay.date) ? video.hud_overlay : null);
      // POIs
      setPois(Array.isArray(video.poi_annotations) ? [...video.poi_annotations] : []);
    }
  }, [video]);

  useEffect(() => {
    if (!video) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [video, onClose]);

  const slugChanged = useMemo(
    () => !!video && slugInput.trim() !== "" && slugInput.trim() !== video.slug,
    [slugInput, video]
  );

  const slugValid = useMemo(
    () => slugInput.trim() === "" || SLUG_RE.test(slugInput.trim()),
    [slugInput]
  );

  const titleChanged = video ? title.trim() !== (video.title || "") : false;
  const descChanged = video ? description !== (video.description || "") : false;
  const visChanged = video ? visibility !== (video.visibility || "private") : false;
  const anythingChanged = titleChanged || descChanged || visChanged || slugChanged;

  if (!video) return null;

  const previewUrl = `${window.location.origin}/v/${slugInput.trim() || video.slug}`;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (slugChanged && !slugValid) {
      setError(
        "Ungültiger Link-Name. Nur Kleinbuchstaben, Ziffern und Bindestriche, " +
          "muss mit Buchstabe/Ziffer anfangen und enden."
      );
      return;
    }

    setSaving(true);
    try {
      const payload = {
        title: title.trim() || video.title,
        description,
        visibility,
      };
      if (slugChanged) payload.new_slug = slugInput.trim();
      // Phase 10G — text overlays (replace HUD overlay UI from Phase 10D).
      payload.text_overlays = textOverlays
        .filter((t) => (t.text || "").trim())
        .map((t) => ({
          id: t.id,
          text: (t.text || "").trim().slice(0, 200),
          start_sec: Math.max(0, Number(t.start_sec) || 0),
          duration_sec: Math.max(1, Math.min(15, Number(t.duration_sec) || 4)),
          position: t.position || "bottom-center",
          font_size: t.font_size || "medium",
          style: t.style || "plain",
          text_color: t.text_color || "#FFFFFF",
        }));
      // Phase 10E — POIs
      payload.poi_annotations = pois.map((p) => ({
        id: p.id,
        start_sec: Math.max(0, Number(p.start_sec) || 0),
        duration_sec: Math.max(1, Math.min(15, Number(p.duration_sec) || 4)),
        target_yaw_deg: Math.max(-180, Math.min(180, Number(p.target_yaw_deg) || 0)),
        target_pitch_deg: Math.max(-90, Math.min(90, Number(p.target_pitch_deg) || 0)),
        hint_text: (p.hint_text || "").trim() || null,
      }));

      const { data } = await api.patch(`/videos/${video.slug}`, payload);
      toast.success("Gespeichert");
      onSaved?.(data);
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/75 backdrop-blur-sm"
      onClick={onClose}
      data-testid="edit-modal-overlay"
    >
      <div
        className="relative w-full max-w-lg rounded-2xl bg-[#17171a] border border-[#2a2a30] p-6 sm:p-7 shadow-[0_24px_80px_-12px_rgba(0,0,0,0.6)] max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        data-testid="edit-modal"
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 inline-flex items-center justify-center w-9 h-9 rounded-md text-[#9a9aa3] hover:text-white hover:bg-[#1f1f23] transition-colors"
          aria-label="Schließen"
          data-testid="edit-modal-close"
        >
          <X size={18} />
        </button>

        <h2 className="text-xl font-bold tracking-tight">Video bearbeiten</h2>
        <p className="text-sm text-[#9a9aa3] mt-1.5">Aktueller Link: /v/{video.slug}</p>

        <form onSubmit={onSubmit} className="mt-6 space-y-5" data-testid="edit-form">
          <div className="space-y-1.5">
            <label htmlFor="edit-title" className="text-xs font-medium text-[#9a9aa3] uppercase tracking-wider">
              Titel
            </label>
            <input
              id="edit-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              className="w-full h-11 px-3.5 rounded-lg bg-[#1f1f23] border border-[#2a2a30] focus:border-[#ff6b35] focus:ring-2 focus:ring-[#ff6b35]/20 outline-none text-white transition-colors"
              data-testid="edit-title-input"
            />
          </div>

          <div className="space-y-1.5" data-testid="edit-slug-block">
            <label
              htmlFor="edit-slug"
              className="text-xs font-medium text-[#9a9aa3] uppercase tracking-wider"
            >
              Link-Name
            </label>
            <div
              className={`flex items-stretch rounded-lg bg-[#0a0a0b] border overflow-hidden transition-colors ${
                slugChanged && !slugValid
                  ? "border-red-500/60"
                  : "border-[#2a2a30] focus-within:border-[#ff6b35]"
              }`}
              data-testid="edit-slug-field"
            >
              <span className="inline-flex items-center px-3 text-sm text-[#6a6a73] font-mono border-r border-[#2a2a30] select-none">
                /v/
              </span>
              <input
                id="edit-slug"
                value={slugInput}
                onChange={(e) => setSlugInput(sanitizeSlug(e.target.value))}
                className="flex-1 h-11 px-3 bg-transparent text-white font-mono text-sm outline-none placeholder:text-[#3a3a40]"
                placeholder={video.slug}
                maxLength={60}
                data-testid="edit-slug-input"
              />
            </div>

            {slugChanged && (
              <div
                className="mt-2 flex items-start gap-2 rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2.5 text-xs text-red-200"
                data-testid="edit-slug-warning"
              >
                <AlertTriangle size={14} className="shrink-0 mt-0.5 text-red-300" />
                <div className="leading-relaxed">
                  <strong className="text-red-100">Wichtig:</strong> Wenn du den Link-Namen
                  änderst, funktionieren bereits geteilte alte Links <strong>NICHT MEHR</strong> —
                  Empfänger sehen &bdquo;Video nicht gefunden&ldquo;. Nur ändern wenn du den
                  Link noch nicht weitergegeben hast.
                </div>
              </div>
            )}

            {slugChanged && slugValid && (
              <p
                className="text-xs text-emerald-300 font-mono break-all"
                data-testid="edit-slug-preview"
              >
                Neuer Link: {previewUrl}
              </p>
            )}

            {slugChanged && !slugValid && (
              <p
                className="text-xs text-red-300"
                data-testid="edit-slug-format-error"
              >
                Nur Kleinbuchstaben, Ziffern und Bindestriche. Muss mit Buchstabe oder
                Ziffer anfangen und enden.
              </p>
            )}

            {!slugChanged && (
              <p className="text-xs text-[#6a6a73]" data-testid="edit-slug-hint">
                Beim Ändern verlieren bereits geteilte Links ihre Funktion.
              </p>
            )}
          </div>

          <div className="space-y-1.5">
            <label htmlFor="edit-desc" className="text-xs font-medium text-[#9a9aa3] uppercase tracking-wider">
              Beschreibung
            </label>
            <textarea
              id="edit-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="w-full px-3.5 py-3 rounded-lg bg-[#1f1f23] border border-[#2a2a30] focus:border-[#ff6b35] focus:ring-2 focus:ring-[#ff6b35]/20 outline-none text-white transition-colors resize-y"
              data-testid="edit-description-input"
            />
          </div>

          <div className="space-y-1.5">
            <span className="text-xs font-medium text-[#9a9aa3] uppercase tracking-wider">
              Sichtbarkeit
            </span>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setVisibility("public")}
                className={`inline-flex items-center justify-center gap-2 h-11 rounded-lg border text-sm font-medium transition-colors ${
                  visibility === "public"
                    ? "bg-[#1f1f23] border-[#ff6b35] text-white"
                    : "bg-[#1f1f23] border-[#2a2a30] text-[#9a9aa3] hover:text-white hover:border-[#3a3a40]"
                }`}
                data-testid="edit-visibility-public"
              >
                <Globe size={15} />
                Öffentlich
              </button>
              <button
                type="button"
                onClick={() => setVisibility("private")}
                className={`inline-flex items-center justify-center gap-2 h-11 rounded-lg border text-sm font-medium transition-colors ${
                  visibility === "private"
                    ? "bg-[#1f1f23] border-[#ff6b35] text-white"
                    : "bg-[#1f1f23] border-[#2a2a30] text-[#9a9aa3] hover:text-white hover:border-[#3a3a40]"
                }`}
                data-testid="edit-visibility-private"
              >
                <Lock size={15} />
                Privat
              </button>
            </div>
          </div>

          {/* Phase 10G — Text-Einblendungen (ersetzt alte HUD-UI) */}
          <details
            open
            className="rounded-lg border border-[#2a2a30] bg-[#0f0f12] open:border-[#3a3a40]"
            data-testid="edit-textoverlays-section"
          >
            <summary className="cursor-pointer select-none px-3.5 py-3 flex items-center gap-2 text-sm font-medium text-white">
              <MapPin size={14} className="text-[#ff6b35]" />
              Text-Einblendungen
              {textOverlays.length > 0 && (
                <span className="ml-auto text-[10px] uppercase tracking-wider text-emerald-300/90">
                  {textOverlays.length}
                </span>
              )}
            </summary>
            <div className="px-3.5 pb-3.5 pt-1 space-y-3">
              {legacyHud && (
                <div
                  className="rounded-md border border-amber-500/30 bg-amber-500/5 p-2.5 text-xs text-amber-200/90 space-y-1.5"
                  data-testid="edit-legacy-hud-notice"
                >
                  <div className="font-medium">Altes Standort/Datum-Overlay aktiv</div>
                  <div className="text-amber-100/70">
                    Wird automatisch im Player angezeigt
                    {legacyHud.location ? ` · 📍 ${legacyHud.location}` : ""}
                    {legacyHud.date ? ` · ${legacyHud.date}` : ""}
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      const next = [...textOverlays];
                      if (legacyHud.location) {
                        next.push({
                          id: `tmp_${Date.now()}_l`,
                          text: legacyHud.location,
                          start_sec: Number(legacyHud.show_at_sec ?? 2.0),
                          duration_sec: Number(legacyHud.duration_sec ?? 4.0),
                          position: legacyHud.position || "bottom-left",
                          font_size: legacyHud.font_size || "medium",
                          style: "plain",
                          text_color: "#FFFFFF",
                        });
                      }
                      if (legacyHud.date) {
                        next.push({
                          id: `tmp_${Date.now()}_d`,
                          text: legacyHud.date,
                          start_sec: Number(legacyHud.show_at_sec ?? 2.0) + 0.1,
                          duration_sec: Number(legacyHud.duration_sec ?? 4.0),
                          position: legacyHud.position === "bottom-left" ? "bottom-right" : "bottom-left",
                          font_size: "small",
                          style: "plain",
                          text_color: "#FFFFFF",
                        });
                      }
                      setTextOverlays(next);
                      setLegacyHud(null);
                    }}
                    className="inline-flex items-center justify-center h-7 px-2.5 rounded text-[11px] font-medium bg-amber-500/20 text-amber-100 hover:bg-amber-500/30"
                    data-testid="edit-legacy-hud-convert"
                  >
                    Übernehmen
                  </button>
                </div>
              )}
              {textOverlays.length === 0 && (
                <p className="text-xs text-[#6a6a73]">
                  Noch keine Einblendungen. Klick auf &bdquo;+ Einblendung hinzufügen&ldquo; unten.
                </p>
              )}
              {textOverlays.map((t, i) => (
                <div
                  key={t.id || i}
                  className="rounded-md border border-[#2a2a30] bg-[#17171a] p-2.5 space-y-2"
                  data-testid={`edit-text-row-${i}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs font-medium text-white">Einblendung #{i + 1}</span>
                    <button
                      type="button"
                      onClick={() => setTextOverlays(textOverlays.filter((_, j) => j !== i))}
                      className="inline-flex items-center justify-center w-7 h-7 rounded text-[#9a9aa3] hover:text-red-300 hover:bg-red-500/10"
                      aria-label="Einblendung löschen"
                      data-testid={`edit-text-delete-${i}`}
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                  <textarea
                    value={t.text || ""}
                    onChange={(e) => updateTextOverlay(setTextOverlays, textOverlays, i, "text", e.target.value.slice(0, 200))}
                    placeholder="Hier am Strand von Seminyak"
                    rows={2}
                    className={`w-full px-3 py-2 rounded-md bg-[#1f1f23] border outline-none text-white text-sm resize-none ${
                      !(t.text || "").trim() ? "border-red-500/40 focus:border-red-500/70" : "border-[#2a2a30] focus:border-[#ff6b35]"
                    }`}
                    data-testid={`edit-text-content-${i}`}
                  />
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    <NumInput
                      label="Start (Sek)"
                      value={t.start_sec ?? 0}
                      step={0.5}
                      min={0}
                      onChange={(v) => updateTextOverlay(setTextOverlays, textOverlays, i, "start_sec", v)}
                      testId={`edit-text-start-${i}`}
                    />
                    <NumInput
                      label="Dauer (Sek)"
                      value={t.duration_sec ?? 4}
                      step={0.5}
                      min={1}
                      max={15}
                      onChange={(v) => updateTextOverlay(setTextOverlays, textOverlays, i, "duration_sec", v)}
                      testId={`edit-text-dur-${i}`}
                    />
                    <div>
                      <label className="text-[11px] uppercase tracking-wider text-[#6a6a73] block mb-1">Position</label>
                      <select
                        value={t.position || "bottom-center"}
                        onChange={(e) => updateTextOverlay(setTextOverlays, textOverlays, i, "position", e.target.value)}
                        className="w-full h-9 px-2 rounded-md bg-[#1f1f23] border border-[#2a2a30] focus:border-[#ff6b35] outline-none text-white text-sm"
                        data-testid={`edit-text-pos-${i}`}
                      >
                        <option value="top-left">Oben links</option>
                        <option value="top-center">Oben Mitte</option>
                        <option value="top-right">Oben rechts</option>
                        <option value="bottom-left">Unten links</option>
                        <option value="bottom-center">Unten Mitte</option>
                        <option value="bottom-right">Unten rechts</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-[11px] uppercase tracking-wider text-[#6a6a73] block mb-1">Größe</label>
                      <select
                        value={t.font_size || "medium"}
                        onChange={(e) => updateTextOverlay(setTextOverlays, textOverlays, i, "font_size", e.target.value)}
                        className="w-full h-9 px-2 rounded-md bg-[#1f1f23] border border-[#2a2a30] focus:border-[#ff6b35] outline-none text-white text-sm"
                        data-testid={`edit-text-size-${i}`}
                      >
                        <option value="small">Klein</option>
                        <option value="medium">Mittel</option>
                        <option value="large">Groß</option>
                      </select>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <div>
                      <label className="text-[11px] uppercase tracking-wider text-[#6a6a73] block mb-1">Stil</label>
                      <div className="flex items-center gap-1.5">
                        {[["plain", "Nur Text"], ["with_background", "Mit Hintergrund"]].map(([v, l]) => (
                          <button
                            key={v}
                            type="button"
                            onClick={() => updateTextOverlay(setTextOverlays, textOverlays, i, "style", v)}
                            className={`flex-1 h-9 rounded-md text-xs font-medium border transition-colors ${
                              (t.style || "plain") === v
                                ? "bg-[#1f1f23] border-[#ff6b35] text-white"
                                : "bg-[#1f1f23] border-[#2a2a30] text-[#9a9aa3] hover:text-white"
                            }`}
                            data-testid={`edit-text-style-${v}-${i}`}
                          >
                            {l}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="text-[11px] uppercase tracking-wider text-[#6a6a73] block mb-1">Textfarbe</label>
                      <div className="flex items-center gap-2 h-9">
                        <input
                          type="color"
                          value={t.text_color || "#FFFFFF"}
                          onChange={(e) => updateTextOverlay(setTextOverlays, textOverlays, i, "text_color", e.target.value.toUpperCase())}
                          className="w-9 h-9 rounded-md border border-[#2a2a30] bg-transparent cursor-pointer"
                          data-testid={`edit-text-color-${i}`}
                        />
                        <span className="text-xs font-mono text-[#9a9aa3]">{(t.text_color || "#FFFFFF").toUpperCase()}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              <button
                type="button"
                onClick={() =>
                  setTextOverlays([
                    ...textOverlays,
                    {
                      id: `tmp_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
                      text: "",
                      start_sec: 0,
                      duration_sec: 4,
                      position: "bottom-center",
                      font_size: "medium",
                      style: "plain",
                      text_color: "#FFFFFF",
                    },
                  ])
                }
                className="inline-flex items-center justify-center gap-2 h-9 px-3 rounded-md border border-[#2a2a30] text-[#9a9aa3] hover:text-white hover:border-[#3a3a40] text-xs"
                data-testid="edit-text-add"
              >
                + Einblendung hinzufügen
              </button>
            </div>
          </details>

          {error && (
            <div
              className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-300"
              data-testid="edit-error"
            >
              {error}
            </div>
          )}


          {/* Phase 10E — POI "Schau hier hin"-Pfeile */}
          <details
            className="rounded-lg border border-[#2a2a30] bg-[#0f0f12] open:border-[#3a3a40]"
            data-testid="edit-poi-section"
          >
            <summary className="cursor-pointer select-none px-3.5 py-3 flex items-center gap-2 text-sm font-medium text-white">
              <Compass size={14} className="text-[#ff6b35]" />
              Schau-hier-hin-Pfeile
              {pois.length > 0 && (
                <span className="ml-auto text-[10px] uppercase tracking-wider text-emerald-300/90">
                  {pois.length}
                </span>
              )}
            </summary>
            <div className="px-3.5 pb-3.5 pt-1 space-y-3">
              <p className="text-xs text-[#9a9aa3] leading-relaxed">
                Pfeil zeigt im Player, wenn der Zuschauer nicht in die angegebene
                Richtung schaut. Yaw: <span className="font-mono">−180 bis 180°</span> (0° vorne,
                90° rechts, −90° links, ±180° hinten). Pitch: <span className="font-mono">−90 bis 90°</span>.
              </p>
              {pois.length === 0 && (
                <p className="text-xs text-[#6a6a73]">
                  Noch keine Pfeile. Klick auf &bdquo;Pfeil hinzufügen&ldquo; unten.
                </p>
              )}
              {pois.map((p, i) => (
                <div
                  key={p.id || i}
                  className="rounded-md border border-[#2a2a30] bg-[#17171a] p-2.5 space-y-2"
                  data-testid={`edit-poi-row-${i}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs font-medium text-white">Pfeil #{i + 1}</span>
                    <button
                      type="button"
                      onClick={() => setPois(pois.filter((_, j) => j !== i))}
                      className="inline-flex items-center justify-center w-7 h-7 rounded text-[#9a9aa3] hover:text-red-300 hover:bg-red-500/10"
                      aria-label="Pfeil löschen"
                      data-testid={`edit-poi-delete-${i}`}
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    <NumInput
                      label="Start (Sek)"
                      value={p.start_sec}
                      step={0.5}
                      min={0}
                      onChange={(v) => updatePoi(setPois, pois, i, "start_sec", v)}
                      testId={`edit-poi-start-${i}`}
                    />
                    <NumInput
                      label="Dauer (Sek)"
                      value={p.duration_sec}
                      step={0.5}
                      min={1}
                      max={15}
                      onChange={(v) => updatePoi(setPois, pois, i, "duration_sec", v)}
                      testId={`edit-poi-dur-${i}`}
                    />
                    <NumInput
                      label="Yaw (°)"
                      value={p.target_yaw_deg}
                      step={5}
                      min={-180}
                      max={180}
                      onChange={(v) => updatePoi(setPois, pois, i, "target_yaw_deg", v)}
                      testId={`edit-poi-yaw-${i}`}
                    />
                    <NumInput
                      label="Pitch (°)"
                      value={p.target_pitch_deg ?? 0}
                      step={5}
                      min={-90}
                      max={90}
                      onChange={(v) => updatePoi(setPois, pois, i, "target_pitch_deg", v)}
                      testId={`edit-poi-pitch-${i}`}
                    />
                  </div>
                  <div>
                    <label className="text-[11px] uppercase tracking-wider text-[#6a6a73] block mb-1">Hinweistext (optional)</label>
                    <input
                      value={p.hint_text || ""}
                      onChange={(e) =>
                        updatePoi(setPois, pois, i, "hint_text", e.target.value.slice(0, 60))
                      }
                      placeholder="z. B. Schau nach rechts"
                      className="w-full h-9 px-3 rounded-md bg-[#1f1f23] border border-[#2a2a30] focus:border-[#ff6b35] outline-none text-white text-sm"
                      data-testid={`edit-poi-hint-${i}`}
                    />
                  </div>
                </div>
              ))}
              <button
                type="button"
                onClick={() =>
                  setPois([
                    ...pois,
                    {
                      id: `tmp_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
                      start_sec: 2.0,
                      duration_sec: 4.0,
                      target_yaw_deg: 90.0,
                      target_pitch_deg: 0.0,
                      hint_text: "",
                    },
                  ])
                }
                className="inline-flex items-center justify-center gap-2 h-9 px-3 rounded-md border border-[#2a2a30] text-[#9a9aa3] hover:text-white hover:border-[#3a3a40] text-xs"
                data-testid="edit-poi-add"
              >
                + Pfeil hinzufügen
              </button>
            </div>
          </details>

          <div className="flex items-center justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center justify-center h-10 px-4 rounded-xl border border-[#2a2a30] text-white font-medium hover:bg-[#1f1f23] transition-colors"
              data-testid="edit-cancel-button"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={saving || (slugChanged && !slugValid)}
              className="inline-flex items-center justify-center gap-2 h-10 px-5 rounded-xl bg-gradient-to-r from-[#ff6b35] to-[#ff3d6e] text-white font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="edit-save-button"
            >
              {saving && <Loader2 size={14} className="animate-spin" />}
              Speichern
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function updatePoi(setPois, pois, idx, key, value) {
  const next = [...pois];
  next[idx] = { ...next[idx], [key]: value };
  setPois(next);
}

function updateTextOverlay(setTextOverlays, list, idx, key, value) {
  const next = [...list];
  next[idx] = { ...next[idx], [key]: value };
  setTextOverlays(next);
}

function NumInput({ label, value, onChange, step = 1, min, max, testId }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-wider text-[#6a6a73] block mb-1">{label}</label>
      <input
        type="number"
        value={value}
        step={step}
        min={min}
        max={max}
        onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
        className="w-full h-9 px-2 rounded-md bg-[#1f1f23] border border-[#2a2a30] focus:border-[#ff6b35] outline-none text-white text-sm tabular-nums"
        data-testid={testId}
      />
    </div>
  );
}
